import { useContext, useEffect, useRef, useState } from 'react';
import './index.css';
import { widget } from '../charting_library';
import Datafeed from './mobileDatafeed';
import { ProfileContext } from '../../../context/ProfileProvider';
import { disconnectMobileChartSocket } from './mobileStreaming';
import {
  bindChartAutoSave,
  CHART_STATE_KEYS,
  loadChartState,
} from '../tvChartStateStorage';

export default function TVChartContainer({ symbol, theme }) {
  const { newStoredTheme } = useContext(ProfileContext);
  const tvWidgetRef = useRef(null);
  const flushChartSaveRef = useRef(null);
  const containerId = "TVChartContainer";
  const Theme = theme;
  const [isReady, setIsReady] = useState(false);
  const isLight = Theme === 'light';
  const bgColor = isLight ? '#ffffff' : '#171a20';
  const scaleTextColor = isLight ? '#000000' : '#ffffff';

  const scaleOverrides = {
    'paneProperties.background': bgColor,
    'paneProperties.backgroundType': 'solid',
    'scalesProperties.backgroundColor': bgColor,
    'scalesProperties.textColor': scaleTextColor,
    'paneProperties.legendProperties.showVolume': false,
  };

  const applyScaleTheme = (tvWidget) => {
    if (!tvWidget) return;
    const pushColors = () => {
      try {
        tvWidget.applyOverrides?.(scaleOverrides);
      } catch (e) {}
      try {
        tvWidget.chart()?.applyOverrides?.(scaleOverrides);
      } catch (e) {}
    };
    try {
      const themeResult = tvWidget.changeTheme?.(isLight ? 'light' : 'dark');
      if (themeResult?.then) {
        themeResult.then(pushColors).catch(pushColors);
      } else {
        pushColors();
      }
    } catch (e) {
      pushColors();
    }
    // Saved layout can re-apply black scale text after ready; re-assert colors.
    setTimeout(pushColors, 0);
    setTimeout(pushColors, 150);
  };

  const initChart = (initSymbol) => {
    if (tvWidgetRef.current) return;

    const savedData = loadChartState(CHART_STATE_KEYS.spot);
    const widgetOptions = {
      symbol: initSymbol,
      load_last_chart: true,
      auto_save_delay: 1,
      ...(savedData ? { saved_data: savedData } : {}),
      interval: '1',
      fullscreen: false,
      timezone: 'Asia/Kolkata',
      container: containerId,
      datafeed: Datafeed,
      has_intraday: true,
      library_path: '/charting_library/',
      pricescale: 1000000,
      intraday_multipliers: ['1', '3', '5', '15', '30', '60'],
      custom_css_url: 'mobileCss/style.css',
      hide_resolution_in_legend: false,
      height: '300px',
      time_frames: [
        { text: '1D', resolution: 'D', description: '1 Day' },
        { text: '1W', resolution: 'W', description: '1 Week' },
        { text: '1M', resolution: 'M', description: '1 Month' },
      ],
      time_scale: { min_bar_spacing: 1 },
      theme: isLight ? "light" : "dark",
      enabled_features: ["drag_scroll"],
      overrides: {
        "paneProperties.background": bgColor,
        "paneProperties.backgroundType": "solid",
        "scalesProperties.fontSize": 14,
        "scalesProperties.fontFamily": "HarmonyOS Sans Regular, sans-serif",
        "scalesProperties.backgroundColor": bgColor,
        "scalesProperties.textColor": scaleTextColor,
        "paneProperties.legendProperties.showLegend": true,
        "paneProperties.legendProperties.showVolume": false,
        "paneProperties.legendProperties.showStudyValues": true,
        "paneProperties.legendProperties.fontSize": 14,
        "paneProperties.legendProperties.fontFamily": "HarmonyOS Sans Regular, sans-serif",
      },
      loading_screen: {
        backgroundColor: bgColor
      },
      disabled_features: [
        "left_toolbar",
        "create_volume_indicator_by_default",
        "use_sessionStorage_for_settings",
        "header_symbol_search",
        "header_interval_dialog_button",
        "header_settings",
        "header_compare",
        "header_undo_redo",
        "header_resolutions",
        "study_dialog_search_control",
        "symbol_info",
        "timeframes_toolbar",
        "pane_context_menu",
        "control_bar",
        "header_chart_type",
        "context_menus",
        "main_series_scale_menu",
        "legend_context_menu"
      ],
      enabled_features: [],
    };

    const tvWidget = new widget(widgetOptions);
    tvWidgetRef.current = tvWidget;
    flushChartSaveRef.current = bindChartAutoSave(tvWidget, CHART_STATE_KEYS.spot);

    tvWidget.onChartReady(() => {
      setIsReady(true);

      const chart = tvWidget.chart();
      applyScaleTheme(tvWidget);
      const studies = chart.getAllStudies?.() || [];
      studies.forEach((study) => {
        if (study?.name?.toLowerCase?.().includes('volume')) {
          chart.removeEntity(study.id);
        }
      });
      // Default study only on fresh charts (avoid duplicating after restore)
      if (!savedData) {
        chart.createStudy('Moving Average', false, false, [14], null, { 'Plot.color': '#FF0000' });
      }

      if (savedData && initSymbol) {
        try {
          chart.setSymbol(initSymbol, () => null);
        } catch (e) {}
      }

      // Custom interval buttons
      tvWidget.headerReady().then(() => {
        const intervals = [
          { value: '1', label: '1 Min' },
          { value: '5', label: '5 Min' },
          { value: '15', label: '15 Min' },
          { value: '30', label: '30 Min' },
          { value: '60', label: '1 Hour' },
          { value: 'D', label: '1 Day' },
          { value: 'W', label: '1 Week' },
          { value: 'M', label: '1 Month' }
        ];
        intervals.forEach(interval => {
          const button = tvWidget.createButton();
          button.classList.add('custom-interval-button');
          button.title = `Switch to ${interval.label}`;
          button.addEventListener('click', () => {
            tvWidget.chart().setResolution(interval.value);
          });
          button.textContent = interval.label;
        });
      });
    });
  };

  useEffect(() => {
    initChart(symbol);
    
    // Cleanup on unmount
    return () => {
      try {
        flushChartSaveRef.current?.();
      } catch (e) {}
      disconnectMobileChartSocket();
      if (tvWidgetRef.current) {
        try {
          tvWidgetRef.current.remove();
          tvWidgetRef.current = null;
        } catch (e) {
          // Widget removal error - safe to ignore
        }
      }
    };
  }, []);

  useEffect(() => {
    if (tvWidgetRef.current) {
      tvWidgetRef.current.onChartReady(() => {
        tvWidgetRef.current.chart().setSymbol(symbol, '1');
      });
    }
  }, [symbol]);

  useEffect(() => {
    if (tvWidgetRef.current) {
      tvWidgetRef.current.onChartReady(() => {
        applyScaleTheme(tvWidgetRef.current);
      });
    }
  }, [Theme]);

  return (
    <div style={{ position: "relative", minHeight: "300px", height: "auto", backgroundColor: bgColor }}>
      {/* Chart */}
      <div
        id={containerId}
        className={Theme === 'light' ? 'light-theme' : 'dark-theme'}
        style={{
          opacity: isReady ? 1 : 0,
          transition: 'opacity 0.1s ease',
          backgroundColor: bgColor,
          height: "100%",
        }}
      />

      {/* Loader */}
      {!isReady && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            background: bgColor + " !important",
            zIndex: 10,
          }}
        >
          <div className="spinner-border" role="status" style={{ width: '2rem', height: '2rem', color: '#d0aa67' }} />
        </div>
      )}
    </div>
  );
}
